import {
  ENDPOINTS,
  RegisterVendorPartyConflictSchema,
  RegisterVendorPartyRequestSchema,
  type RegisterVendorPartyConflict,
  type RegisterVendorPartyRequest,
  type RegisterVendorPartyResponse,
} from '@clm/shared-types';

import { apiPost } from './client';
import { ApiError } from './errors';

/**
 * `register-vendor-party` — CLM-78 (R-76-2), the interim IR-03 path.
 *
 * The body is the vendor's typed **legal name and nothing else**. The key is
 * minted server-side from the reserved `V…` space (FR-14): there is no field to
 * carry one, so a form that tried would fail the `.strict()` parse here and
 * never reach `apiPost`. The response is parsed, not asserted, and carries the
 * party the picker can select at once, the confirmed `Manual` alias (= the typed
 * name, so FR-29 substitutes it from the first document) and the audit receipt
 * of the alias write (rule 7).
 *
 * A **409** is not a failure of the form: `vendor_exists` offers the party that
 * already carries this name (by display name **or** active alias) so the user
 * picks it instead of registering a twin; `vendor_key_space_exhausted` means the
 * `V` + two-letter walk found no free key. {@link vendorRegistrationConflict}
 * reads either from the `ApiError` body; anything else yields `null` and the
 * surface falls back to the generic copy.
 */
const REGISTER_ENDPOINT = ENDPOINTS['register-vendor-party'];

export async function registerVendorParty(
  request: RegisterVendorPartyRequest,
  signal?: AbortSignal,
): Promise<RegisterVendorPartyResponse> {
  const body = RegisterVendorPartyRequestSchema.parse(request);
  const raw = await apiPost<unknown>(REGISTER_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return REGISTER_ENDPOINT.response.parse(raw);
}

/** The typed 409 body, when the error carries one. */
export function vendorRegistrationConflict(error: unknown): RegisterVendorPartyConflict | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const parsed = RegisterVendorPartyConflictSchema.safeParse(error.body);
  return parsed.success ? parsed.data : null;
}
